import { Route } from '@angular/router';

import { EBOFeatures } from '@summit/core';

import { CanDeactivateGuard } from './helpers/guards/can-deactivate-guard';
import { DashboardGuard } from './helpers/guards/dashboard-guard';
import { FlagGuard } from './helpers/guards/flag-dialog/flag-guard';
import { LoginGuard } from './interface/login-guard';
import { EboPermissions } from './models/ebo/shared/policies/ebo-permissions.enum';

export const AppRoutes: Route[] = [
  {
    path: '',
    loadComponent: () =>
      import(`./framework/components/app-layout/app.layout.component`).then((m) => m.AppLayoutComponent),
    children: [
      {
        path: '',
        loadComponent: () => import(`./framework/home/home.component`).then((m) => m.HomeComponent),
        pathMatch: 'full',
        canActivate: [DashboardGuard]
      },
      /*
      {
        path: 'test',
        // component: AboutComponent,
        // loadComponent: () => import('@summit/dialog').then(m => m.PrimeConfirmationComponent),
        loadComponent: () =>
        import(`../../../../libs/summit/form/src/lib/components/form-input-checkbox-day-of-week`).then(
          (m) => m.FormInputCheckboxDayOfWeekComponent
          )
          // resolve: { dlgRef: DialogResolverService },
          // children: [
            //   {
              //     path: '0',
              //     component: PrimeConfirmationComponent,
              //     data: {
                //       dlg: {
                  //         data: { name: 'Sample Dialog #a.0' },
                  //         redirectPath: ['dashboard'],
                  //         width: '500px'
                  //       } as DialogRouteConfig
                  //     }
                  //   }
                  // ]
      },
      */
      {
        path: 'sandbox',
        canActivate: [LoginGuard],
        loadComponent: () => import(`./components/dev/sandbox/sandbox.component`).then((m) => m.SandboxComponent),
        data: { title: 'Sandbox' }
      },
      {
        path: 'signup',
        canActivate: [LoginGuard],
        loadComponent: () => import(`./framework/sign-up/sign-up.component`).then((m) => m.SignUpComponent),
        data: { title: 'Sign Up' }
      },
      {
        path: 'about',
        loadComponent: () => import(`./framework/about/about.component`).then((m) => m.AboutComponent),
        data: { preload: true, title: 'About', flags: ['core'] as EBOFeatures[] }
      },
      {
        path: 'updates',
        data: { preload: true, title: 'Updates' },
        children: [
          {
            path: '',
            loadComponent: () =>
              import(`./framework/components/update-card-view/update-card-view.component`).then(
                (m) => m.UpdatesCardViewComponent
              ),
            pathMatch: 'full',
            data: { preload: true, title: 'Updates' }
          },
          {
            path: ':id',
            canActivate: [LoginGuard],
            loadComponent: () =>
              import(`./framework/components/update-edit/update-edit.component`).then((m) => m.UpdateEditComponent),
            canDeactivate: [CanDeactivateGuard],
            data: { preload: true, title: 'Edit Update' }
          }
        ]
      },
      {
        path: 'accounts/resetpassword',
        loadComponent: () =>
          import(`./framework/login/components/password-reset/password-reset.component`).then(
            (m) => m.PasswordResetComponent
          ),
        data: {}
      },
      {
        path: 'assets',
        canActivate: [LoginGuard, FlagGuard],
        loadComponent: () =>
          import('./modules/assets/asset-grid/asset-grid.component').then((m) => m.AssetGridComponent),
        pathMatch: 'full',
        data: { title: 'Assets: [View]', flags: ['core'] as EBOFeatures[] }
      },
      {
        path: 'assets/:id',
        canActivate: [LoginGuard, FlagGuard],
        loadComponent: () =>
          import('./modules/assets/asset-edit/asset-edit.component').then((m) => m.AssetEditComponent),
        canDeactivate: [CanDeactivateGuard],
        data: { title: 'Assets: [Entry]', flags: ['core'] as EBOFeatures[] }
      },
      {
        path: 'automations',
        canActivate: [LoginGuard, FlagGuard],
        children: [
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () =>
              import('./modules/automations/components/automation-grid/automation-grid.component').then(
                (m) => m.AutomationGridComponent
              ),
            data: { title: 'Automations: [View]', flags: ['core'] as EBOFeatures[] }
          },
          {
            path: ':id',
            loadComponent: () =>
              import('./modules/automations/components/automation-edit/automation-edit.component').then(
                (m) => m.AutomationEditComponent
              ),
            canDeactivate: [CanDeactivateGuard],
            data: { title: 'Auto: [Entry]', flags: ['core'] as EBOFeatures[] }
          }
        ]
      },
      {
        path: 'bonuses',
        canActivate: [LoginGuard, FlagGuard],
        data: { preload: true, flags: ['payroll'] as EBOFeatures[] },
        children: [
          {
            path: '',
            loadComponent: () =>
              import(`./modules/payroll/components/bonus-grid/bonus-grid.component`).then((m) => m.BonusGridComponent),
            pathMatch: 'full',
            data: {
              title: 'Bonuses: [View]',
              flags: ['payroll'] as EBOFeatures[],
              preload: EboPermissions.PermissionsManager
            }
          },
          {
            path: ':id',
            loadComponent: () =>
              import(`./modules/payroll/components/bonuses-edit/bonuses-edit.component`).then(
                (m) => m.BonusesEditComponent
              ),
            canDeactivate: [CanDeactivateGuard],
            data: {
              title: 'Bonuses: [Entry]',
              flags: ['payroll'] as EBOFeatures[],
              preload: EboPermissions.PermissionsManager
            }
          }
        ]
      },
      {
        path: 'communications',
        canActivate: [LoginGuard, FlagGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import(`./modules/communications/subscription-grid/subscription-grid.component`).then(
                (m) => m.SubscriptionGridComponent
              ),
            pathMatch: 'full',
            data: { title: 'Subscriptions: [View]', flags: ['core'] as EBOFeatures[] }
          },
          {
            path: 'messages',
            loadComponent: () =>
              import(`./modules/communications/e-message-grid/e-message-grid.component`).then(
                (m) => m.EMessageGridComponent
              ),
            data: { title: 'Mgs Logs: [View]', flags: ['payroll'] as EBOFeatures[] }
          },
          {
            path: 'messages/:id',
            loadComponent: () =>
              import(`./modules/communications/e-message-send/e-message-send.component`).then(
                (m) => m.EMessageSendComponent
              ),
            data: { title: 'Mgs Logs: [View]', flags: ['payroll'] as EBOFeatures[] }
          },
          {
            path: 'daily',
            canActivate: [LoginGuard, FlagGuard],
            loadComponent: () =>
              import(`./modules/communications/daily-journal-grid/daily-journal-grid.component`).then(
                (m) => m.DailyJournalGridComponent
              ),
            pathMatch: 'full',
            data: {
              title: 'Daily: [View]',
              flags: ['core'] as EBOFeatures[],
              preload: EboPermissions.PermissionsBackOffice
            }
          },
          {
            path: 'daily/:id',
            canActivate: [LoginGuard, FlagGuard],
            loadComponent: () =>
              import(`./modules/communications/daily-journal-edit/daily-journal-edit.component`).then(
                (m) => m.DailyJournalEditComponent
              ),
            data: {
              title: 'Daily: [Entry]',
              flags: ['core'] as EBOFeatures[],
              preload: EboPermissions.PermissionsBackOffice
            }
          },
          {
            path: 'subscriptions',
            canActivate: [LoginGuard, FlagGuard],
            loadComponent: () =>
              import(`./modules/communications/subscription-grid/subscription-grid.component`).then(
                (m) => m.SubscriptionGridComponent
              ),
            pathMatch: 'full',
            data: {
              title: 'Subscriptions: [View]',
              flags: ['core'] as EBOFeatures[],
              preload: EboPermissions.PermissionsBackOffice
            }
          },
          {
            path: 'subscriptions/:id',
            canActivate: [LoginGuard, FlagGuard],
            canDeactivate: [CanDeactivateGuard],
            loadComponent: () =>
              import(`./modules/communications/subscriptions-edit/subscriptions-edit.component`).then(
                (m) => m.SubscriptionsEditComponent
              ),
            data: { title: 'Subscriptions: [Entry]', flags: ['core'] as EBOFeatures[] }
          }
        ]
      },

      {
        path: 'dashboard',
        canActivate: [LoginGuard],
        loadComponent: () =>
          import(`./framework/dashboard/components/dashboard/dashboard.component`).then((m) => m.DashboardComponent),
        data: { preload: true, flags: ['core'] as EBOFeatures[] }
      },
      {
        path: 'fuel',
        canActivate: [LoginGuard],
        data: { preload: EboPermissions.PermissionsBackOffice, flags: ['core'] as EBOFeatures[] },
        children: [
          {
            path: 'ivmr',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./modules/fuel/components/ivmr-entry-grid/ivmr-entry-grid.component').then(
                    (m) => m.IvmrEntryGridComponent
                  ),
                pathMatch: 'full',
                data: { title: 'IVMR: [View]', flags: ['core'] as EBOFeatures[] }
              },
              {
                path: ':id',
                loadComponent: () =>
                  import('./modules/fuel/components/ivmr-entry-view/ivmr-entry-view.component').then(
                    (m) => m.IvmrEntryViewComponent
                  ),
                canDeactivate: [CanDeactivateGuard],
                data: { title: 'IVMR: [Entry]', flags: ['core'] as EBOFeatures[] }
              }
            ]
          }
        ]
      },
      {
        path: 'help',
        canActivate: [LoginGuard],
        data: { preload: true, flags: ['core'] as EBOFeatures[] },
        children: [
          {
            path: '',
            loadComponent: () => import('./framework/help/components/help/help.component').then((m) => m.HelpComponent),
            pathMatch: 'full',
            data: { title: 'Help', flags: ['core'] as EBOFeatures[] }
          },
          {
            path: 'features',
            loadComponent: () =>
              import('./framework/help/components/help-features/help-features.component').then(
                (m) => m.HelpFeaturesComponent
              ),
            data: { title: 'Help: Features', flags: ['core'] as EBOFeatures[] }
          }
        ]
      },
      {
        path: 'myaccount',
        canActivate: [LoginGuard, FlagGuard],
        data: { flags: ['core'] as EBOFeatures[] },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./framework/my-account/my-account-edit/my-account-edit.component').then(
                (m) => m.MyAccountEditComponent
              ),
            canDeactivate: [CanDeactivateGuard],
            pathMatch: 'full',
            data: { preload: true, title: 'My Account', flags: ['core'] as EBOFeatures[] }
          },
          {
            path: ':id',
            loadComponent: () =>
              import('./framework/my-account/my-account-edit/my-account-edit.component').then(
                (m) => m.MyAccountEditComponent
              ),
            canDeactivate: [CanDeactivateGuard],
            data: { preload: true, title: 'My Account', flags: ['core'] as EBOFeatures[] }
          }
        ]
      },
      {
        path: 'calendar',
        canActivate: [LoginGuard, FlagGuard],
        loadComponent: () => import(`./framework/calendar/calendar.component`).then((m) => m.CalendarComponent),
        data: { preload: true, flags: ['scheduler'] as EBOFeatures[] }
      },
      {
        path: 'login',
        canActivate: [LoginGuard],
        loadComponent: () => import(`./framework/login/components/login/login.component`).then((m) => m.LoginComponent),
        data: {}
      },
      {
        path: 'aliases',
        canActivate: [LoginGuard, FlagGuard],
        data: { preload: true, flags: ['core'] as EBOFeatures[] },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./modules/aliases/components/truck-alias-grid/truck-alias-grid.component').then(
                (m) => m.TruckAliasGridComponent
              ),
            pathMatch: 'full',
            data: {
              title: 'FedEx Alias: [View]',
              flags: ['core'] as EBOFeatures[],
              preload: EboPermissions.PermissionsMechanic
            }
          },
          {
            path: ':id',
            loadComponent: () =>
              import('./modules/aliases/components/alias-edit/alias-edit.component').then((m) => m.AliasEditComponent),
            canDeactivate: [CanDeactivateGuard],
            data: {
              title: 'FedEx Alias: [Entry]',
              flags: ['core'] as EBOFeatures[],
              preload: EboPermissions.PermissionsMechanic
            }
          }
        ]
      },
      {
        path: 'companies',
        canActivate: [LoginGuard, FlagGuard],
        loadComponent: () =>
          import('./modules/organization/company-grid/company-grid.component').then((m) => m.CompanyGridComponent),
        pathMatch: 'full',
        data: {
          title: 'Companies: [View]',
          flags: ['core'] as EBOFeatures[],
          preload: EboPermissions.PermissionsBackOffice
        }
      },
      {
        path: 'companies/contracts',
        canActivate: [LoginGuard, FlagGuard],
        loadComponent: () =>
          import('./modules/organization/contract-grid/contract-grid.component').then((m) => m.ContractGridComponent),
        pathMatch: 'full',
        data: {
          title: 'Contracts: [View]',
          flags: ['core'] as EBOFeatures[],
          preload: EboPermissions.PermissionsBackOffice
        }
      },
      {
        path: 'companies/contracts/:id',
        canActivate: [LoginGuard, FlagGuard],
        loadComponent: () =>
          import('./modules/organization/contract-edit/contract-edit.component').then((m) => m.ContractEditComponent),
        canDeactivate: [CanDeactivateGuard],
        data: { title: 'Contracts: [Entry]', flags: ['core'] as EBOFeatures[] }
      },
      {
        path: 'companies/:id',
        canActivate: [LoginGuard, FlagGuard],
        loadComponent: () =>
          import('./modules/organization/company-edit/company-edit.component').then((m) => m.CompaniesEditComponent),
        canDeactivate: [CanDeactivateGuard],
        data: { title: 'Companies: [Entry]', flags: ['core'] as EBOFeatures[] }
      },
      {
        path: 'driverlogs',
        data: { flags: ['driverLogs'] as EBOFeatures[] },
        children: [
          {
            path: 'clock-in',
            loadComponent: () =>
              import(`./modules/digital-logs/components/asks/ask-wizard-clock-in/ask-wizard-clock-in.component`).then(
                (m) => m.AskWizardClockInComponent
              ),
            canDeactivate: [CanDeactivateGuard],
            data: { preload: true, title: 'Clock In: [Entry]', flags: ['driverLogs'] as EBOFeatures[] }
          },
          {
            path: 'change-flow/:id',
            loadComponent: () =>
              import(
                `./modules/digital-logs/components/asks/ask-wizard-change-flow/ask-wizard-change-flow.component`
              ).then((m) => m.AskWizardChangeFlowComponent),
            canDeactivate: [CanDeactivateGuard],
            data: { title: 'Change Status: [Entry]', flags: ['driverLogs'] as EBOFeatures[] }
          },
          {
            path: 'change-flow',
            redirectTo: '/dashboard'
          },
          {
            path: 'change-status/:id',
            loadComponent: () =>
              import(
                `./modules/digital-logs/components/asks/ask-wizard-change-status/ask-wizard-change-status.component`
              ).then((m) => m.AskWizardChangeStatusComponent),
            canDeactivate: [CanDeactivateGuard],
            data: { preload: true, title: 'Change Status: [Entry]', flags: ['driverLogs'] as EBOFeatures[] }
          },
          {
            path: 'change-status',
            redirectTo: '/dashboard'
          },
          {
            path: 'clock-out/:id',
            loadComponent: () =>
              import(`./modules/digital-logs/components/asks/ask-wizard-clock-out/ask-wizard-clock-out.component`).then(
                (m) => m.AskWizardClockOutComponent
              ),
            canDeactivate: [CanDeactivateGuard],
            data: { preload: true, title: 'Clock Out: [Entry]', flags: ['driverLogs'] as EBOFeatures[] }
          },
          {
            path: 'clock-out',
            redirectTo: '/dashboard'
          },
          {
            path: 'wizard/:id',
            loadComponent: () =>
              import(`./modules/digital-logs/components/asks/ask-wizard/ask-wizard.component`).then(
                (m) => m.AskWizardComponent
              ),
            canDeactivate: [CanDeactivateGuard],
            data: { preload: true, title: 'Pre/Post: [Entry]', flags: ['driverLogs'] as EBOFeatures[] }
          },

          {
            path: ':id',
            loadComponent: () =>
              import(`./modules/digital-logs/components/asks/ask-edit/ask-edit.component`).then(
                (m) => m.AskEditComponent
              ),
            canDeactivate: [CanDeactivateGuard],
            data: { preload: true, title: 'Log: [Entry]', flags: ['driverLogs'] as EBOFeatures[] }
          },
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () =>
              import(`./modules/digital-logs/components/asks/ask-grid/ask-grid.component`).then(
                (m) => m.AskGridComponent
              ),
            data: { preload: true, title: 'Logs: [View]', flags: ['driverLogs'] as EBOFeatures[] }
          }
        ]
      },

      {
        path: 'divisions',
        canActivate: [LoginGuard, FlagGuard],
        data: { flags: ['core'] as EBOFeatures[] },
        children: [
          {
            path: 'legwages',
            canActivate: [FlagGuard],
            loadComponent: () =>
              import('./modules/organization/division-leg-wage-set-grid/division-leg-wage-set-grid.component').then(
                (m) => m.DivisionLegWageSetGridComponent
              ),
            //component: DivisionLegWageListComponent,
            pathMatch: 'full',
            data: {
              title: 'Leg Wages: [View]',
              flags: ['payroll'] as EBOFeatures[],
              preload: EboPermissions.PermissionsBackOffice
            }
          },
          {
            path: 'legwages/:id',
            canActivate: [FlagGuard],
            loadComponent: () =>
              import('./modules/organization/division-leg-wage-set-edit/division-leg-wage-set-edit.component').then(
                (m) => m.DivisionLegWageSetEditComponent
              ),
            canDeactivate: [CanDeactivateGuard],
            data: {
              title: 'Leg Rule Set: [Entry]',
              flags: ['payroll'] as EBOFeatures[],
              preload: EboPermissions.PermissionsBackOffice
            }
          },
          {
            path: '',
            loadComponent: () =>
              import('./modules/organization/division-grid.component').then((m) => m.DivisionGridComponent),
            pathMatch: 'full',
            data: {
              title: 'Divisions: [View]',
              flags: ['payroll'] as EBOFeatures[],
              preload: EboPermissions.PermissionsBackOffice
            }
          },
          {
            path: ':id',
            loadComponent: () =>
              import('./modules/organization/division-edit/division-edit.component').then(
                (m) => m.DivisionEditComponent
              ),

            canDeactivate: [CanDeactivateGuard],
            data: {
              title: 'Divisions: [Entry]',
              flags: ['payroll'] as EBOFeatures[],
              preload: EboPermissions.PermissionsBackOffice
            }
          }
        ]
      },
      {
        path: 'expenses',
        canActivate: [LoginGuard, FlagGuard],
        data: { preload: true, flags: ['driverLogs'] as EBOFeatures[] },
        children: [
          {
            path: 'wizard/:id',
            canDeactivate: [CanDeactivateGuard],
            loadComponent: () =>
              import(`./modules/digital-logs/components/expense-wizard/expense-wizard.component`).then(
                (m) => m.ExpenseWizardComponent
              ),
            data: { preload: true, flags: ['driverLogs'] as EBOFeatures[] }
          },
          {
            path: ':id',
            canDeactivate: [CanDeactivateGuard],
            loadComponent: () =>
              import(`./modules/digital-logs/components/expense-edit/expense-edit.component`).then(
                (m) => m.ExpenseEditComponent
              ),
            data: { preload: true, flags: ['driverLogs'] as EBOFeatures[] }
          },
          {
            path: '',
            pathMatch: 'full',
            canActivate: [LoginGuard, FlagGuard],
            loadComponent: () =>
              import(`./modules/digital-logs/components/expense-grid/expenses-grid.component`).then(
                (m) => m.ExpenseGridComponent
              ),
            data: { preload: true, flags: ['driverLogs'] as EBOFeatures[] }
          }
        ]
      },
      {
        path: 'externallinks',
        canActivate: [LoginGuard, FlagGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./modules/external-links/components/external-link-grid.component').then(
                (m) => m.ExternalLinkGridComponent
              ),
            pathMatch: 'full',
            data: { title: 'External Links: [View]', flags: ['core'] as EBOFeatures[] }
          },
          {
            path: ':id',
            loadComponent: () =>
              import('./modules/external-links/components/external-link-edit/external-link-edit.component').then(
                (m) => m.ExternalLinkEditComponent
              ),
            canDeactivate: [CanDeactivateGuard],
            data: { title: 'External Link: [Entry]', flags: ['core'] as EBOFeatures[] }
          }
        ],
        data: { preload: true, flags: ['core'] as EBOFeatures[] }
      },
      {
        path: 'employees',
        canActivate: [LoginGuard, FlagGuard],
        data: { preload: true, flags: ['core'] as EBOFeatures[] },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./modules/employees/components/employee-grid/employee-grid.component').then(
                (m) => m.EmployeeGridComponent
              ),
            pathMatch: 'full',
            data: {
              title: 'Employees: [View]',
              flags: ['core'] as EBOFeatures[],
              preload: EboPermissions.PermissionsLeadDriver
            }
          },
          {
            path: ':id',
            loadComponent: () =>
              import('./modules/employees/components/employee-edit/employee-edit.component').then(
                (m) => m.EmployeeEditComponent
              ),
            canDeactivate: [CanDeactivateGuard],
            data: { title: 'Employee: [Entry]', flags: ['core'] as EBOFeatures[] }
          }
        ]
      },
      {
        path: 'flatrates',
        canActivate: [LoginGuard, FlagGuard],
        loadComponent: () =>
          import('./modules/payroll/components/flat-rate-grid/flat-rate-grid.component').then(
            (m) => m.FlatRateGridComponent
          ),
        pathMatch: 'full',
        data: {
          title: 'Flat Rate: [View]',
          flags: ['payroll'] as EBOFeatures[],
          preload: EboPermissions.PermissionsManager
        }
      },
      {
        path: 'flatrates/:id',
        canActivate: [LoginGuard, FlagGuard],
        canDeactivate: [CanDeactivateGuard],
        loadComponent: () =>
          import('./modules/payroll/components/flat-rate-edit/flat-rate-edit.component').then(
            (m) => m.FlatRateEditComponent
          ),
        pathMatch: 'full',
        data: {
          title: 'Flat Rate: [Entry]',
          flags: ['payroll'] as EBOFeatures[],
          preload: EboPermissions.PermissionsManager
        }
      },
      {
        path: 'gpslegs',
        canActivate: [LoginGuard, FlagGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./modules/gps-legs/components/gps-leg-grid.component').then((m) => m.GpsLegGridComponent),
            pathMatch: 'full',
            data: { title: 'GPS Legs: [View]', flags: ['payroll'] as EBOFeatures[] }
          },
          {
            path: ':id',
            loadComponent: () =>
              import('./modules/gps-legs/components/gps-leg-edit/gps-leg-edit.component').then(
                (m) => m.GpsLegEditComponent
              ),
            canDeactivate: [CanDeactivateGuard],
            data: { title: 'GPS Legs: [Entry]', flags: ['payroll'] as EBOFeatures[] }
          }
        ],
        data: { preload: true, flags: ['payroll'] as EBOFeatures[] }
      },
      {
        path: 'holidaypay',
        canActivate: [LoginGuard, FlagGuard],
        data: { flags: ['payroll'] as EBOFeatures[] },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./modules/payroll/components/holiday-pay-grid/holiday-pay-grid.component').then(
                (m) => m.HolidayPayGridComponent
              ),
            pathMatch: 'full',
            data: {
              title: 'Holiday Pay: [View]',
              flags: ['payroll'] as EBOFeatures[],
              preload: EboPermissions.PermissionsManager
            }
          },
          {
            path: ':id',
            loadComponent: () =>
              import('./modules/payroll/components/holiday-pay-edit/holiday-pay-edit.component').then(
                (m) => m.HolidayPayEditComponent
              ),
            canDeactivate: [CanDeactivateGuard],
            data: {
              title: 'Holiday Pay: [Entry]',
              flags: ['payroll'] as EBOFeatures[],
              preload: EboPermissions.PermissionsManager
            }
          }
        ]
      },
      {
        path: 'hubs/:id',
        loadComponent: () =>
          import('./components/places/hubs/hub-edit/hub-edit.component').then((m) => m.HubEditComponent),
        canDeactivate: [CanDeactivateGuard],
        data: { title: 'Hubs: [Entry]', flags: ['core'] as EBOFeatures[] }
      },
      {
        path: 'hubs',
        loadComponent: () =>
          import('./components/places/hubs/hub-grid/hub-grid.component').then((m) => m.HubGridComponent),
        pathMatch: 'full',
        data: { title: 'Hubs: [View]', flags: ['core'] as EBOFeatures[] }
      },
      {
        path: 'import',
        canActivate: [LoginGuard, FlagGuard],
        loadComponent: () =>
          import('./modules/import/components/import-wizard/import.component').then((m) => m.ImportComponent),
        pathMatch: 'full',
        data: { title: 'Import', flags: ['core'] as EBOFeatures[] }
      },
      {
        path: 'integrity',
        canActivate: [LoginGuard, FlagGuard],
        loadComponent: () =>
          import('./modules/integrity/components/integrity/integrity.component').then((m) => m.IntegrityComponent),
        pathMatch: 'full',
        data: { title: 'Import', flags: ['core'] as EBOFeatures[] }
      },
      {
        path: 'miscwork',
        pathMatch: 'full',
        canActivate: [LoginGuard, FlagGuard],
        loadComponent: () =>
          import('./modules/digital-logs/components/misc-work-grid.component').then((m) => m.MiscWorkGridComponent),
        data: { title: 'Misc Work: [View]', flags: ['driverLogs'] as EBOFeatures[], preload: true }
      },
      {
        path: 'miscwork/:id',
        canActivate: [LoginGuard, FlagGuard],
        canDeactivate: [CanDeactivateGuard],
        loadComponent: () =>
          import('./modules/digital-logs/components/misc-work-edit/misc-work-edit.component').then(
            (m) => m.MiscWorkEditComponent
          ),
        data: { title: 'Misc Work: [Entry]', flags: ['driverLogs'] as EBOFeatures[], preload: true }
      },
      {
        path: 'organization',
        canActivate: [LoginGuard, FlagGuard],
        loadComponent: () =>
          import(`./modules/organization/org-overview/org-overview.component`).then((m) => m.OrgOverviewComponent),
        data: {
          title: 'Organization ',
          flags: ['core'] as EBOFeatures[],
          preload: EboPermissions.PermissionsBackOffice
        }
      },
      {
        path: 'oauth',
        canActivate: [LoginGuard],
        children: [
          {
            path: 'motive/:id',
            loadComponent: () =>
              import(`./components/oauth/motive/motive-oauth/motive-oauth.component`).then(
                (m) => m.MotiveOauthComponent
              ),
            data: { flags: ['core'] as EBOFeatures[] }
          },
          {
            path: 'motivedev/:id',
            loadComponent: () =>
              import(`./components/oauth/motive/motive-oauth-dev/motive-oauth-dev.component`).then(
                (m) => m.MotiveOauthDevComponent
              ),
            data: { flags: ['core'] as EBOFeatures[] }
          },
          {
            path: '**', // catch all
            redirectTo: '/'
          }
        ]
      },
      {
        path: 'payroll',
        pathMatch: 'full',
        loadComponent: () =>
          import(`./modules/payroll/components/payroll-grid/payroll-grid.component`).then(
            (m) => m.PayrollGridComponent
          ),
        data: {
          title: 'Payroll: [View]',
          flags: ['payroll'] as EBOFeatures[],
          preload: EboPermissions.PermissionsManager
        }
      },
      {
        path: 'payroll/custom',
        pathMatch: 'full',
        loadComponent: () =>
          import(`./modules/payroll/components/payroll-custom-grid/payroll-custom-grid.component`).then(
            (m) => m.PayrollCustomGridComponent
          ),
        data: {
          title: 'Custom Payroll Export',
          flags: ['payroll'] as EBOFeatures[],
          preload: EboPermissions.PermissionsPayroll
        }
      },
      {
        path: 'payroll/dashboard',
        pathMatch: 'full',
        loadComponent: () =>
          import(`./modules/payroll/components/payroll-dashboard/payroll-dashboard.component`).then(
            (m) => m.PayrollDashboardComponent
          ),
        data: {
          title: 'Payroll Dashboard',
          flags: ['payroll'] as EBOFeatures[],
          preload: EboPermissions.PermissionsManager
        }
      },
      {
        path: 'payroll/advances/:id',
        loadComponent: () =>
          import(`./modules/payroll/components/payroll-advance-edit/payroll-advance-edit.component`).then(
            (m) => m.PayrollAdvanceEditComponent
          ),
        data: {
          title: 'Advance: [Entry]',
          flags: ['payroll'] as EBOFeatures[],
          preload: EboPermissions.PermissionsManager
        }
      },
      {
        path: 'payroll/advances',
        pathMatch: 'full',
        loadComponent: () =>
          import('./modules/payroll/components/payroll-advance-grid/payroll-advance-grid.component').then(
            (m) => m.PayrollAdvanceGridComponent
          ),
        data: {
          title: 'Advance: [View]',
          flags: ['payroll'] as EBOFeatures[],
          preload: EboPermissions.PermissionsManager
        }
      },
      {
        path: 'payroll/breakdown',
        pathMatch: 'full',
        loadComponent: () =>
          import('./modules/payroll/components/pay-breakdown/pay-breakdown.component').then(
            (m) => m.PayBreakdownComponent
          ),
        data: { title: 'Pay Breakdown: [View]', flags: ['payroll'] as EBOFeatures[], preload: true }
      },
      {
        path: 'payroll/employeeadj/:id',
        loadComponent: () =>
          import(`./modules/payroll/components/employee-adj-pay-edit/employee-adj-pay-edit.component`).then(
            (m) => m.EmployeeAdjPayEditComponent
          ),
        data: {
          title: 'Emp. Adj: [Entry]',
          flags: ['payroll'] as EBOFeatures[],
          preload: EboPermissions.PermissionsManager
        }
      },
      {
        path: 'payroll/employeeadj',
        pathMatch: 'full',
        loadComponent: () =>
          import(`./modules/payroll/components/employee-adj-pay-grid/employee-adj-pay-grid.component`).then(
            (m) => m.EmployeeAdjPayGridComponent
          ),
        //component: EmployeeAdjPayListComponent,
        data: {
          title: 'Emp. Adj.: [View]',
          flags: ['payroll'] as EBOFeatures[],
          preload: EboPermissions.PermissionsManager
        }
      },
      {
        path: 'payroll/incentives/:id',
        loadComponent: () =>
          import(`./modules/payroll/components/incentive-edit/incentive-edit.component`).then(
            (m) => m.IncentiveEditComponent
          ),
        data: {
          title: 'Incentive: [Entry]',
          flags: ['payroll'] as EBOFeatures[],
          preload: EboPermissions.PermissionsPayroll
        }
      },
      {
        path: 'payroll/incentives',
        pathMatch: 'full',
        loadComponent: () =>
          import(`./modules/payroll/components/incentive-grid/incentive-grid.component`).then(
            (m) => m.IncentiveGridComponent
          ),
        //component: IncentiveListComponent,
        data: {
          title: 'Incentives: [View]',
          flags: ['payroll'] as EBOFeatures[],
          preload: EboPermissions.PermissionsPayroll
        }
      },
      {
        path: 'payroll/spotspay/:id',
        loadComponent: () =>
          import(`./modules/payroll/components/spot-pay-edit/spot-pay-edit.component`).then(
            (m) => m.SpotPayEditComponent
          ),
        data: {
          title: 'Spot Pay: [Entry]',
          flags: ['payroll'] as EBOFeatures[],
          preload: EboPermissions.PermissionsPayroll
        }
      },
      {
        path: 'payroll/spotspay',
        pathMatch: 'full',
        loadComponent: () =>
          import(`./modules/payroll/components/spot-pay-grid/spot-pay-grid.component`).then(
            (m) => m.SpotPayGridComponent
          ),
        data: {
          title: 'Spot Pay: [View]',
          flags: ['payroll'] as EBOFeatures[],
          preload: EboPermissions.PermissionsPayroll
        }
      },

      {
        path: 'payroll/routepay/:id',
        loadComponent: () =>
          import(`./modules/payroll/components/route-pay-edit/route-pay-edit.component`).then(
            (m) => m.RoutePayEditComponent
          ),
        data: {
          title: 'Route Pay: [Entry]',
          flags: ['payroll'] as EBOFeatures[],
          preload: EboPermissions.PermissionsPayroll
        }
      },
      {
        path: 'payroll/routepay',
        pathMatch: 'full',
        loadComponent() {
          return import(`./modules/payroll/components/route-pay-grid/route-pay-grid.component`).then(
            (m) => m.RoutePayGridComponent
          );
        },
        data: {
          title: 'Route Pay: [View]',
          flags: ['payroll'] as EBOFeatures[],
          preload: EboPermissions.PermissionsPayroll
        }
      },
      {
        path: 'payroll/perdiem',
        pathMatch: 'full',
        loadComponent: () =>
          import(`./modules/payroll/components/per-diem-grid/per-diem-grid.component`).then(
            (m) => m.PerDiemGridComponent
          ),
        data: {
          title: 'Per Diem: [View]',
          flags: ['payroll'] as EBOFeatures[],
          preload: EboPermissions.PermissionsPayroll
        }
      },
      {
        path: 'payroll/perdiem/:id',
        loadComponent: () =>
          import(`./modules/payroll/components/per-diem-edit/per-diem-edit.component`).then(
            (m) => m.PerDiemEditComponent
          ),
        data: {
          title: 'Per Diem: [Entry]',
          flags: ['payroll'] as EBOFeatures[],
          preload: EboPermissions.PermissionsPayroll
        }
      },
      {
        path: 'payroll/:id',
        loadComponent: () =>
          import(`./modules/payroll/components/payroll-edit/payroll-edit.component`).then(
            (m) => m.PayrollEditComponent
          ),
        canDeactivate: [CanDeactivateGuard],
        data: {
          title: 'Payroll: [Entry]',
          flags: ['payroll'] as EBOFeatures[],
          preload: EboPermissions.PermissionsPayroll
        }
      },
      {
        path: 'payroll',
        pathMatch: 'full',
        canActivate: [LoginGuard, FlagGuard],
        loadComponent: () =>
          import(`./modules/payroll/components/payroll-grid/payroll-grid.component`).then(
            (m) => m.PayrollGridComponent
          ),
        data: { keepFilter: true, flags: ['payroll'] as EBOFeatures[], preload: EboPermissions.PermissionsPayroll }
      },
      {
        path: 'places',
        children: [
          {
            path: 'locations',
            children: [
              {
                path: ':id',
                loadComponent: () =>
                  import('./components/places/locations/e-location-edit/e-location-edit.component').then(
                    (m) => m.ELocationEditComponent
                  ),
                canDeactivate: [CanDeactivateGuard],
                data: { title: 'Location: [Entry]', flags: ['core'] as EBOFeatures[] }
              },
              {
                path: '',
                loadComponent: () =>
                  import('./components/places/locations/e-location-grid/e-location-grid.component').then(
                    (m) => m.ELocationGridComponent
                  ),
                pathMatch: 'full',
                data: { title: 'Locations: [View]', flags: ['core'] as EBOFeatures[] }
              }
            ]
          },
          {
            path: 'properties',
            children: [
              {
                path: ':id',
                loadComponent: () =>
                  import('./components/places/properties/property-edit/property-edit.component').then(
                    (m) => m.PropertyEditComponent
                  ),
                canDeactivate: [CanDeactivateGuard],
                data: { title: 'Property: [Entry]', flags: ['core'] as EBOFeatures[] }
              },
              {
                path: '',
                loadComponent: () =>
                  import('./components/places/properties/property-grid/property-grid.component').then(
                    (m) => m.PropertyGridComponent
                  ),
                pathMatch: 'full',
                data: { title: 'Properties: [View]', flags: ['core'] as EBOFeatures[] }
              }
            ]
          },
          {
            path: 'terminals',
            children: [
              {
                path: ':id',
                loadComponent: () =>
                  import('./components/places/terminals/terminal-edit/terminal-edit.component').then(
                    (m) => m.TerminalEditComponent
                  ),
                canDeactivate: [CanDeactivateGuard],
                data: { title: 'Terminal: [Entry]', flags: ['core'] as EBOFeatures[] }
              },
              {
                path: '',
                loadComponent: () =>
                  import('./components/places/terminals/terminal-grid/terminal-grid.component').then(
                    (m) => m.TerminalGridComponent
                  ),
                pathMatch: 'full',
                data: { title: 'Terminals: [View]', flags: ['core'] as EBOFeatures[] }
              }
            ]
          }
        ]
      },

      {
        path: 'processes',
        canActivate: [LoginGuard, FlagGuard],
        pathMatch: 'full',
        loadComponent: () =>
          import(`./modules/process/components/e-process-grid/e-process-grid.component`).then(
            (m) => m.EProcessGridComponent
          ),
        data: { title: 'Process: [View]', preload: true, flags: ['core'] as EBOFeatures[] }
      },
      {
        path: 'processes/maint-request/:id',
        loadComponent: () =>
          import('./modules/process/components/maint-request/maint-request.component').then(
            (m) => m.MaintRequestComponent
          ),
        pathMatch: 'full',
        data: { title: 'Maint. Requests: [View]', flags: ['core'] as EBOFeatures[] }
      },
      { path: 'processes/maint-request', pathMatch: 'full', redirectTo: 'processes' },
      {
        path: 'processes/maint-request/:id',
        loadComponent: () =>
          import('./modules/process/components/maint-request/maint-request.component').then(
            (m) => m.MaintRequestComponent
          ),
        pathMatch: 'full',
        data: { title: 'Maint. Requests: [View]', flags: ['core'] as EBOFeatures[] }
      },
      { path: 'processes/supply-request', pathMatch: 'full', redirectTo: 'processes' },
      {
        path: 'processes/supply-request/:id',
        loadComponent: () =>
          import('./modules/process/components/supply-request/supply-request.component').then(
            (m) => m.SupplyRequestComponent
          ),
        pathMatch: 'full',
        data: { title: 'Supply. Requests: [View]', flags: ['core'] as EBOFeatures[] }
      },
      { path: 'processes/time-off', pathMatch: 'full', redirectTo: 'processes' },
      {
        path: 'processes/time-off/:id',
        loadComponent: () =>
          import('./modules/process/components/time-off-edit/time-off-edit.component').then(
            (m) => m.TimeOffEditComponent
          ),
        pathMatch: 'full',
        data: { title: 'Time Off Request: [Entry]', flags: ['scheduler'] as EBOFeatures[], preload: true }
      },
      {
        path: 'processes/onboarding/:id',
        pathMatch: 'full',
        loadComponent: () =>
          import(`./modules/process/components/on-boarding/on-boarding.component`).then((m) => m.OnBoardingComponent),
        data: { flags: ['core'] as EBOFeatures[] }
      },
      {
        path: 'processes/:id',
        loadComponent: () =>
          import('./modules/process/components/e-process-edit/e-process-edit.component').then(
            (m) => m.ProcessEditComponent
          ),
        pathMatch: 'full',
        data: { title: 'Process: [Entry]', flags: ['core'] as EBOFeatures[], preload: true }
      },
      {
        path: 'files',
        canActivate: [LoginGuard, FlagGuard],
        loadComponent: () => import(`@summit/pdf-page/pdf-page.component`).then((m) => m.PdfComponent),
        data: { title: 'Files', preload: true, keepFilter: true, flags: ['payroll'] as EBOFeatures[] }
      },
      {
        path: 'maintenance',
        canActivate: [LoginGuard, FlagGuard],
        children: [
          {
            path: 'events/:id',
            canActivate: [LoginGuard, FlagGuard],
            canDeactivate: [CanDeactivateGuard],
            loadComponent: () =>
              import(`./modules/maintenance/components/maint-event-edit/maint-event-edit.component`).then(
                (m) => m.MaintEventEditComponent
              ),
            data: {
              title: 'Maint. Events: [Entry]',
              flags: ['maintBasic'] as EBOFeatures[],
              preload: EboPermissions.PermissionsMechanic
            }
          },
          {
            path: 'events',
            canActivate: [LoginGuard, FlagGuard],
            loadComponent: () =>
              import(`./modules/maintenance/components/maint-event-grid.component`).then(
                (m) => m.MaintEventGridComponent
              ),
            data: {
              title: 'Maint. Events: [View]',
              flags: ['maintBasic'] as EBOFeatures[],
              preload: EboPermissions.PermissionsMechanic
            }
          },
          {
            path: 'metrics',
            canActivate: [LoginGuard, FlagGuard],
            loadComponent: () =>
              import(`./modules/maintenance/components/maint-event-metric/maint-event-metric-grid`).then(
                (m) => m.MaintEventMetricGrid
              ),
            data: {
              title: 'Maint. Events: [View]',
              flags: ['maintBasic'] as EBOFeatures[],
              preload: EboPermissions.PermissionsBackOffice
            }
          },
          {
            path: 'items/:id',
            canActivate: [LoginGuard, FlagGuard],
            canDeactivate: [CanDeactivateGuard],
            loadComponent: () =>
              import(`./modules/maintenance/components/maint-item-edit/maint-item-edit.component`).then(
                (m) => m.MaintItemEditComponent
              ),
            data: { title: 'Maint. Items: [Entry]', preload: true, flags: ['maintBasic'] as EBOFeatures[] }
          },
          {
            path: 'items',
            canActivate: [LoginGuard, FlagGuard],
            loadComponent: () =>
              import(`./modules/maintenance/components/maint-item-grid.component`).then(
                (m) => m.MaintItemGridComponent
              ),
            data: { title: 'Maint. Items: [View]', preload: true, flags: ['maintBasic'] as EBOFeatures[] }
          },
          {
            path: 'odometers',
            canActivate: [LoginGuard, FlagGuard],
            loadComponent: () =>
              import(`./modules/maintenance/components/odometer-grid/odometer-grid.component`).then(
                (m) => m.OdometerGridComponent
              ),
            data: {
              title: 'Odometers: [View]',
              flags: ['maintBasic'] as EBOFeatures[],
              preload: EboPermissions.PermissionsMechanic
            }
          },
          {
            path: 'plans/:id',
            canActivate: [LoginGuard, FlagGuard],
            canDeactivate: [CanDeactivateGuard],
            loadComponent: () =>
              import(`./modules/maintenance/components/maint-plan-edit/maint-plan-edit.component`).then(
                (m) => m.MaintPlanEditComponent
              ),
            data: { title: 'Maint. Plans: [Entry]', flags: ['maintBasic'] as EBOFeatures[] }
          },
          {
            path: 'plans',
            canActivate: [LoginGuard, FlagGuard],
            loadComponent: () =>
              import(`./modules/maintenance/components/maint-plan-grid.component`).then(
                (m) => m.MaintPlanGridComponent
              ),
            data: { title: 'Maint. Plans: [View]', flags: ['maintBasic'] as EBOFeatures[] }
          },
          {
            path: 'board',
            canActivate: [LoginGuard, FlagGuard],
            loadComponent: () =>
              import(`./modules/maintenance/components/maint-board/maint-board.component`).then(
                (m) => m.MaintBoardComponent
              ),
            data: {
              title: 'Maint. Board: [View]',
              flags: ['maintBasic'] as EBOFeatures[],
              preload: EboPermissions.PermissionsMechanic
            }
          },
          {
            path: '',
            canActivate: [LoginGuard, FlagGuard],
            loadComponent: () =>
              import(`./modules/maintenance/components/maint-status-grid/maint-status-grid.component`).then(
                (m) => m.MaintStatusGridComponent
              ),
            data: {
              title: 'Maint. Status: [View]',
              flags: ['maintBasic'] as EBOFeatures[],
              preload: EboPermissions.PermissionsMechanic
            }
          }
        ]
      },

      {
        path: 'reconcile',
        canActivate: [LoginGuard, FlagGuard],
        children: [
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () =>
              import(`./modules/digital-logs/components/reconcile-master/reconcile-master.component`).then(
                (m) => m.ReconcileMasterComponent
              ),
            data: {
              title: 'Reconcile: [View]',
              flags: ['payroll'] as EBOFeatures[],
              preload: EboPermissions.PermissionsBackOffice
            }
          },
          {
            path: 'disputes',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./modules/digital-logs/components/dispute-grid/dispute-grid.component').then(
                    (m) => m.DisputeGridComponent
                  ),
                pathMatch: 'full',
                data: {
                  title: 'Disputes: [View]',
                  flags: ['driverLogs'] as EBOFeatures[],
                  preload: EboPermissions.PermissionsBackOffice
                }
              },
              {
                path: 'wizard/:id',
                loadComponent: () =>
                  import('./modules/digital-logs/components/dispute-wizard/dispute-wizard.component').then(
                    (m) => m.DisputeWizardComponent
                  ),
                canDeactivate: [CanDeactivateGuard],
                data: {
                  title: 'Disputes: [Entry]',
                  flags: ['driverLogs'] as EBOFeatures[],
                  preload: EboPermissions.PermissionsBackOffice
                }
              },
              {
                path: ':id',
                loadComponent: () =>
                  import('./modules/digital-logs/components/dispute-edit/dispute-edit.component').then(
                    (m) => m.DisputeEditComponent
                  ),
                canDeactivate: [CanDeactivateGuard],
                data: { title: 'Disputes: [Entry]', flags: ['driverLogs'] as EBOFeatures[] }
              }
            ]
          },
          {
            path: 'missingrevenue',
            pathMatch: 'full',
            loadComponent: () =>
              import(`./modules/digital-logs/components/missing-revenue-grid/missing-revenue-grid.component`).then(
                (m) => m.MissingRevenueGridComponent
              ),
            data: {
              title: 'Missing Rev.: [View]',
              flags: ['payroll'] as EBOFeatures[],
              preload: EboPermissions.PermissionsBackOffice
            }
          },
          {
            path: 'teamvalidation',
            pathMatch: 'full',
            loadComponent: () =>
              import(
                `./modules/digital-logs/components/reconcile-team-validate/reconcile-team-validate.component`
              ).then((m) => m.ReconcileTeamValidateComponent),
            data: { title: 'Team Validation', flags: ['payroll'] as EBOFeatures[] }
          },
          {
            path: ':id',
            loadComponent: () =>
              import('./modules/digital-logs/components/reconcile-edit/reconcile-edit.component').then(
                (m) => m.ReconcileEditComponent
              ),
            canDeactivate: [CanDeactivateGuard],
            data: { title: 'Reconcile: [Entry]', flags: ['payroll'] as EBOFeatures[] }
          }
        ]
      },
      {
        path: 'reconcilerules/:id',
        loadComponent: () =>
          import('./modules/reconcile-rules/components/reconcile-rules-edit/reconcile-rules-edit.component').then(
            (m) => m.ReconcileRulesEditComponent
          ),
        canActivate: [LoginGuard, FlagGuard],
        canDeactivate: [CanDeactivateGuard],
        data: { title: 'ReconcileRules: [Entry]', flags: ['driverLogs'] as EBOFeatures[] }
      },
      {
        path: 'reconcilerules',
        loadComponent: () =>
          import('./modules/reconcile-rules/components/reconcile-rule-grid.component').then(
            (m) => m.ReconcileRuleGridComponent
          ),
        pathMatch: 'full',
        data: { title: 'ReconcileRules: [View]', flags: ['driverLogs'] as EBOFeatures[] }
      },
      {
        path: 'reportcard',
        canActivate: [LoginGuard, FlagGuard],
        loadComponent: () =>
          import('./modules/report-card/components/report-card-home/report-card-home.component').then(
            (m) => m.ReportCardHomeComponent
          ),
        pathMatch: 'full',
        data: { title: 'Report Card', flags: ['core'] as EBOFeatures[] }
      },
      {
        path: 'reportcard/fedexpoints',
        canActivate: [LoginGuard, FlagGuard],
        loadComponent: () =>
          import(`./modules/report-card/components/truck-alias-point-grid/truck-alias-point-grid.component`).then(
            (m) => m.TruckAliasPointGridComponent
          ),
        //component: TruckAliasPointsListComponent,
        data: { title: 'FedEx Points [View]', flags: ['core'] as EBOFeatures[] },
        pathMatch: 'full'
      },
      {
        path: 'reports',
        canActivate: [LoginGuard, FlagGuard],
        data: { flags: ['core'] as EBOFeatures[] },
        children: [
          {
            path: 'routesbydrivers',
            //component: ManagersRouteListComponent,
            loadComponent: () =>
              import(`./modules/reports-manager/components/routes-by-driver-grid.component`).then(
                (m) => m.RoutesByDriverGridComponent
              ),
            canDeactivate: [CanDeactivateGuard],
            data: { title: 'Routes By Drivers', flags: ['payroll'] as EBOFeatures[] }
          },
          {
            path: 'spotsbydrivers',
            loadComponent: () =>
              import(`./modules/reports-manager/components/spots-by-driver-grid.component`).then(
                (m) => m.SpotsByDriverGridComponent
              ),
            canDeactivate: [CanDeactivateGuard],
            data: { title: 'Spots By Drivers', flags: ['payroll'] as EBOFeatures[] }
          },
          {
            path: 'spotanalysis',
            loadComponent: () =>
              import(`./modules/reports-manager/components/spot-analysis/spot-analysis-grid.component`).then(
                (m) => m.SpotAnalysisGridComponent
              ),
            canDeactivate: [CanDeactivateGuard],
            data: { title: 'Spot Analysis', flags: ['payroll'] as EBOFeatures[] }
          },
          {
            path: 'managersreport',
            loadComponent: () =>
              import('./modules/reports-manager/components/manager-employee-grid.component').then(
                (m) => m.ManagerEmployeeGridComponent
              ),
            data: { title: 'Managers Report [View]', flags: ['payroll'] as EBOFeatures[] }
          },
          {
            path: 'weeklyactivity',
            loadComponent: () =>
              import('./modules/reports-manager/components/employee-miles-per-day/employee-miles-per-day-grid.component').then(
                (m) => m.EmployeeMilesPerDayGridComponent
              ),
            data: { title: 'Weekly Activity', flags: ['payroll'] as EBOFeatures[] }
          },
          {
            path: 'profitability',
            data: { flags: ['payroll'] as EBOFeatures[] },
            children: [
              {
                path: 'byalias',
                loadComponent: () =>
                  import('./modules/reports-manager/components/profitability-by-alias/profitability-by-alias-grid.component').then(
                    (m) => m.ProfitabilityByAliasGridComponent
                  ),
                data: { title: 'Profitability By Unit Number [View]', flags: ['payroll'] as EBOFeatures[] }
              },
              {
                path: 'bycompany',
                loadComponent: () =>
                  import('./modules/reports-manager/components/profitability-report-by-company/profitability-by-company-grid.component').then(
                    (m) => m.ProfitabilityByCompanyGridComponent
                  ),
                data: { title: 'Profitability By Company [View]', flags: ['payroll'] as EBOFeatures[] }
              },
              {
                path: 'bycompanyandtruck',
                loadComponent: () =>
                  import('./modules/reports-manager/components/profitability-by-company-and-truck/profitability-by-company-and-truck-grid.component').then(
                    (m) => m.ProfitabilityByCompanyAndTruckGridComponent
                  ),
                data: { title: 'Profitability By Company And Truck [View]', flags: ['payroll'] as EBOFeatures[] }
              },
              {
                path: 'byroute',
                loadComponent: () =>
                  import('./modules/reports-manager/components/profitability-by-route-grid.component').then(
                    (m) => m.ProfitabilityByRouteGridComponent
                  ),
                data: { title: 'Profitability By Route [View]', flags: ['payroll'] as EBOFeatures[] }
              },
              {
                path: 'byall',
                loadComponent: () =>
                  import('./modules/reports-manager/components/profitability-by-all/profitability-by-all-grid.component').then(
                    (m) => m.ProfitabilityByAllGridComponent
                  ),
                data: { title: 'Profitability By All [View]', flags: ['payroll'] as EBOFeatures[] }
              },

              {
                path: 'bytruck',
                loadComponent: () =>
                  import('./modules/reports-manager/components/profitability-by-truck-grid.component').then(
                    (m) => m.ProfitabilityByTruckGridComponent
                  ),
                //component: ProfitabilityByTruckListComponent,
                data: { title: 'Profitability By Truck [View]', flags: ['payroll'] as EBOFeatures[] }
              },
              {
                path: 'byemployee',
                loadComponent: () =>
                  import('./modules/reports-manager/components/profitability-by-employee-grid.component').then(
                    (m) => m.ProfitabilityByEmployeeGridComponent
                  ),
                data: { title: 'Profitability By Employee [View]', flags: ['payroll'] as EBOFeatures[] }
              }
            ]
          },
          {
            path: 'workerscomp/wage',
            loadComponent: () =>
              import('./modules/reports-manager/components/workers-comp-wage-grid/workers-comp-wage-grid.component').then(
                (m) => m.WorkersCompWageGridComponent
              ),
            data: { title: 'Workers Comp [View]', flags: ['payroll'] as EBOFeatures[] }
          },
          {
            path: '',
            loadComponent: () =>
              import('./modules/reports-manager/components/report-dashboard/report-dashboard.component').then(
                (m) => m.ReportDashboardComponent
              ),
            pathMatch: 'full',
            data: {
              title: 'Report Dashboard',
              flags: ['payroll'] as EBOFeatures[],
              preload: EboPermissions.PermissionsBackOffice
            }
          }
        ]
      },
      {
        path: 'routes',
        canActivate: [LoginGuard, FlagGuard],
        data: { flags: ['core'] as EBOFeatures[] },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./modules/routes/components/route-grid.component').then((m) => m.RouteGridComponent),
            pathMatch: 'full',
            data: { title: 'Routes: [View]', flags: ['core'] as EBOFeatures[] }
          },
          {
            path: 'overview',
            loadComponent: () =>
              import('./modules/routes/components/linehaul-run-overview-grid.component').then(
                (m) => m.LinehaulRunOverviewGridComponent
              ),
            data: { title: 'Linehaul Overview: [View]', flags: ['core'] as EBOFeatures[] }
          },
          {
            path: 'linehaulruns',
            loadComponent: () =>
              import('./modules/routes/components/linehaul-run-grid.component').then((m) => m.LinehaulRunGridComponent),
            data: { title: 'Linehaul Run: [View]', flags: ['core'] as EBOFeatures[] }
          },
          {
            path: 'autoassign/:id',
            loadComponent: () =>
              import('./modules/routes/components/route-auto-assign-edit/route-auto-assign-edit.component').then(
                (m) => m.RouteAutoAssignEditComponent
              ),
            data: { title: 'Auto Assign: [View]', flags: ['driverLogs'] as EBOFeatures[] }
          },
          {
            path: 'autoassign',
            loadComponent: () =>
              import('./modules/routes/components/route-auto-assign-grid/route-auto-assign-grid.component').then(
                (m) => m.RouteAutoAssignGridComponent
              ),
            data: { title: 'Auto Assign: [View]', flags: ['driverLogs'] as EBOFeatures[] }
          },
          {
            path: 'groups/:id',
            loadComponent: () =>
              import('./modules/routes/components/route-group-edit/route-group-edit.component').then(
                (m) => m.RouteGroupEditComponent
              ),
            data: { title: 'Auto Assign: [View]', flags: ['driverLogs'] as EBOFeatures[] }
          },
          {
            path: 'groups',
            loadComponent: () =>
              import('./modules/routes/components/route-group-grid/route-group-grid.component').then(
                (m) => m.RouteGroupGridComponent
              ),
            data: { title: 'Auto Assign: [View]', flags: ['driverLogs'] as EBOFeatures[] }
          },
          {
            path: ':id',
            loadComponent: () =>
              import('./modules/routes/components/routes-edit/routes-edit.component').then(
                (m) => m.RoutesEditComponent
              ),
            canDeactivate: [CanDeactivateGuard],
            data: { title: 'Routes: [Entry]', flags: ['core'] as EBOFeatures[] }
          }
        ]
      },
      {
        path: 'schedules',
        canActivate: [LoginGuard, FlagGuard],
        data: { flags: ['scheduler'] as EBOFeatures[] },
        children: [
          {
            path: '',
            loadComponent: () =>
              import(`./modules/schedules/components/schedule-leg-grid/schedule-leg-grid.component`).then(
                (m) => m.ScheduleLegGridComponent
              ),
            pathMatch: 'full',
            data: {
              title: 'Schedules: [View]',
              flags: ['scheduler'] as EBOFeatures[],
              preload: EboPermissions.PermissionsAny
            }
          },
          {
            path: 'dispatchboard',
            loadComponent: () =>
              import(`./modules/schedules/components/dispatch-board/dispatch-board.component`).then(
                (m) => m.DispatchBoardComponent
              ),
            data: {
              title: 'Dispatch',
              flags: ['scheduler'] as EBOFeatures[],
              preload: EboPermissions.PermissionsDispatch | EboPermissions.PermissionsLeadDriver
            }
          },

          {
            path: 'dispatch',
            loadComponent: () =>
              import(`./modules/schedules/components/dispatch-grid/dispatch-grid.component`).then(
                (m) => m.DispatchGridComponent
              ),
            data: {
              title: 'Dispatch',
              flags: ['scheduler'] as EBOFeatures[],
              preload: EboPermissions.PermissionsLeadDriver
            }
          },

          {
            path: 'livedispatch',
            loadComponent: () =>
              import(`./modules/schedules/components/live-dispatch/live-dispatch.component`).then(
                (m) => m.LiveDispatchComponent
              ),
            pathMatch: 'full',
            data: {
              title: 'Live Dispatch',
              flags: ['scheduler'] as EBOFeatures[],
              preload: EboPermissions.PermissionsLeadDriver
            }
          },

          {
            path: 'builder',
            loadComponent: () =>
              import(`./modules/schedules/components/schedule-builder/schedule-builder.component`).then(
                (m) => m.ScheduleBuilderComponent
              ),
            pathMatch: 'full',
            data: {
              title: 'Schedule: Builder',
              flags: ['scheduler'] as EBOFeatures[],
              preload: EboPermissions.PermissionsLeadDriver
            }
          },
          {
            path: 'dispatchdraft',
            loadComponent: () =>
              import(`./modules/schedules/components/departure-draft-grid/departure-draft-grid.component`).then(
                (m) => m.DepartureDraftGridComponent
              ),
            pathMatch: 'full',
            data: { title: 'Dispatch (Draft)', flags: ['scheduler'] as EBOFeatures[] }
          },
          {
            path: 'board',
            loadComponent: () =>
              import(`./modules/schedules/components/schedule-board/schedule-board.component`).then(
                (m) => m.ScheduleBoardComponent
              ),
            pathMatch: 'full',
            data: {
              title: 'Schedule: Published',
              flags: ['scheduler'] as EBOFeatures[],
              preload: EboPermissions.PermissionsLeadDriver
            }
          },
          {
            path: 'list',
            loadComponent: () =>
              import(`./modules/schedules/components/schedule-leg-published-grid.component`).then(
                (m) => m.ScheduleLegPublishedGridComponent
              ),
            pathMatch: 'full',
            data: { title: 'Schedule: Published', flags: ['scheduler'] as EBOFeatures[] }
          },
          {
            path: 'board/employee',
            loadComponent: () =>
              import(
                `./modules/schedules/components/schedule-board/by-employee/schedule-board-employee.component`
              ).then((m) => m.ScheduleBoardEmployeeComponent),
            pathMatch: 'prefix',
            data: { title: 'Schedule: Published', flags: ['scheduler'] as EBOFeatures[] }
          },
          {
            path: 'dispatch',
            loadComponent: () =>
              import(`./modules/schedules/components/departure-grid/departure-grid.component`).then(
                (m) => m.DepartureGridComponent
              ),
            pathMatch: 'full',
            data: { title: 'Dispatch', flags: ['scheduler'] as EBOFeatures[] }
          },
          {
            path: ':id',
            loadComponent: () =>
              import(`./modules/schedules/components/schedule-leg-edit/schedule-leg-edit.component`).then(
                (m) => m.ScheduleLegEditComponent
              ),
            canDeactivate: [CanDeactivateGuard],
            data: { title: 'Schedule: [Entry]', flags: ['scheduler'] as EBOFeatures[] }
          }
        ]
      },
      {
        path: 'settlements',
        canActivate: [LoginGuard, FlagGuard],
        children: [
          {
            path: '',
            pathMatch: 'full',
            canActivate: [LoginGuard, FlagGuard],
            loadComponent: () =>
              import('./modules/settlements/components/settlement-grid.component').then(
                (m) => m.SettlementGridComponent
              ),
            data: {
              title: 'Settlements: [View]',
              flags: ['core'] as EBOFeatures[],
              preload: EboPermissions.PermissionsBackOffice
            }
          },
          {
            path: 'dashboard',
            canActivate: [LoginGuard, FlagGuard],
            loadComponent: () =>
              import('./modules/settlements/components/settlement-dashboard/settlement-dashboard.component').then(
                (m) => m.SettlementDashboardComponent
              ),
            pathMatch: 'full',
            data: { title: 'Settlement Dashboard', flags: ['core'] as EBOFeatures[] }
          },
          {
            path: 'fuelbreakdown',
            canActivate: [LoginGuard, FlagGuard],
            loadComponent: () =>
              import('./modules/settlements/components/fuel-breakdown-grid/fuel-breakdown-grid.component').then(
                (m) => m.FuelBreakdownGridComponent
              ),
            pathMatch: 'full',
            data: { title: 'Fuel Breakdown', flags: ['core'] as EBOFeatures[] }
          },
          {
            path: 'fuelbreakdownbytruck',
            canActivate: [LoginGuard, FlagGuard],
            loadComponent: () =>
              import('./modules/settlements/components/fuel-breakdown-grid/fuel-breakdown-by-truck-grid.component').then(
                (m) => m.FuelBreakdownByTruckGridComponent
              ),
            pathMatch: 'full',
            data: { title: 'Fuel Breakdown', flags: ['core'] as EBOFeatures[] }
          },
          {
            path: 'fuelbreakdownbyowner',
            canActivate: [LoginGuard, FlagGuard],
            loadComponent: () =>
              import('./modules/settlements/components/fuel-breakdown-grid/fuel-breakdown-by-owner-grid.component').then(
                (m) => m.FuelBreakdownByOwnerGridComponent
              ),
            pathMatch: 'full',
            data: { title: 'Fuel Breakdown', flags: ['core'] as EBOFeatures[] }
          },
          {
            path: 'sections',
            canActivate: [LoginGuard, FlagGuard],
            loadComponent: () =>
              import('./modules/settlements/components/settlement-section-grid/settlement-section-grid.component').then(
                (m) => m.SettlementSectionGridComponent
              ),
            pathMatch: 'full',
            data: { title: 'Settlement Sections: [View]', flags: ['core'] as EBOFeatures[] }
          },
          {
            path: 'settlementlineitems',
            canActivate: [LoginGuard, FlagGuard],
            loadComponent: () =>
              import('./modules/settlements/components/settlement-line-item-grid/settlement-line-item-grid.component').then(
                (m) => m.SettlementLineItemGridComponent
              ),
            pathMatch: 'full',
            data: { title: 'Settlement Line items', flags: ['core'] as EBOFeatures[] }
          },
          {
            path: 'spots',
            canActivate: [LoginGuard, FlagGuard],
            loadComponent: () =>
              import('./modules/settlements/components/settlement-spot-redirect/settlement-spot-redirect.component').then(
                (m) => m.SettlementSpotRedirectComponent
              ),
            pathMatch: 'full',
            data: { title: 'Settlement Spots: [View]', flags: ['core'] as EBOFeatures[] }
          },
          {
            path: 'spots/:id',
            canActivate: [LoginGuard, FlagGuard],
            loadComponent: () =>
              import('./modules/settlements/components/settlement-spot-redirect/settlement-spot-redirect.component').then(
                (m) => m.SettlementSpotRedirectComponent
              ),
            data: { title: 'Settlement Spots: [Entry]', flags: ['core'] as EBOFeatures[] }
          },
          {
            path: 'expenses',
            canActivate: [LoginGuard, FlagGuard],
            loadComponent: () =>
              import('./modules/settlements/components/settlement-expense-grid.component').then(
                (m) => m.SettlementExpenseGridComponent
              ),
            pathMatch: 'full',
            data: { title: 'Settlement Expenses: [View]', flags: ['core'] as EBOFeatures[] }
          },
          {
            path: 'expenses/:id',
            canActivate: [LoginGuard, FlagGuard],
            loadComponent: () =>
              import('./modules/settlements/components/settlement-expense-edit/settlement-expense-edit.component').then(
                (m) => m.SettlementExpenseEditComponent
              ),
            data: { title: 'Settlement Expenses: [Entry]', flags: ['core'] as EBOFeatures[] }
          },
          {
            path: ':id',
            canActivate: [LoginGuard, FlagGuard],
            loadComponent: () =>
              import('./modules/settlements/components/settlement-edit/settlement-edit.component').then(
                (m) => m.SettlementEditComponent
              ),
            data: { title: 'Settlements: [Entry]', flags: ['core'] as EBOFeatures[] }
          }
        ]
      },
      {
        path: 'sicktime',
        canActivate: [LoginGuard, FlagGuard],
        data: { flags: ['payroll'] as EBOFeatures[] },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./modules/sick-time/components/sick-time-grid.component').then((m) => m.SickTimeGridComponent),
            pathMatch: 'full',
            data: {
              title: 'Sick Time: [View]',
              flags: ['payroll'] as EBOFeatures[],
              preload: EboPermissions.PermissionsPayroll
            }
          },
          {
            path: ':id',
            loadComponent: () =>
              import('./modules/sick-time/components/sick-time-edit/sick-time-edit.component').then(
                (m) => m.SickTimeEditComponent
              ),
            canDeactivate: [CanDeactivateGuard],
            data: {
              title: 'Sick Time: [Entry]',
              flags: ['payroll'] as EBOFeatures[],
              preload: EboPermissions.PermissionsPayroll
            }
          }
        ]
      },
      {
        path: 'spots',
        canActivate: [LoginGuard, FlagGuard],
        data: { flags: ['core'] as EBOFeatures[] },
        children: [
          {
            path: 'runs',
            loadComponent: () =>
              import('./modules/spots/components/spot-run-grid.component').then((m) => m.SpotRunGridComponent),
            canActivate: [FlagGuard],
            pathMatch: 'full',
            data: {
              title: 'Spot Run: [View]',
              flags: ['core'] as EBOFeatures[],
              preload: EboPermissions.PermissionsBackOffice
            }
          },
          {
            path: 'runs/:id',
            loadComponent: () =>
              import('./modules/spots/components/spot-run-edit/spot-run-edit.component').then(
                (m) => m.SpotRunEditComponent
              ),
            canActivate: [FlagGuard],
            canDeactivate: [CanDeactivateGuard],
            data: {
              title: 'Spot Run: [Entry]',
              flags: ['core'] as EBOFeatures[],
              preload: EboPermissions.PermissionsBackOffice
            }
          },
          {
            path: '',
            //    component: SpotAskListComponent,
            loadComponent: () =>
              import('./modules/digital-logs/components/spot-grid-redirect/spot-grid-redirect.component').then(
                (m) => m.SpotGridRedirectComponent
              ),
            canActivate: [FlagGuard],
            pathMatch: 'full',
            data: {
              title: 'Spot: [View]',
              flags: ['driverLogs'] as EBOFeatures[],
              preload: EboPermissions.PermissionsBackOffice
            }
          }
        ]
      },
      {
        path: 'states',
        canActivate: [LoginGuard, FlagGuard],
        data: { flags: ['core'] as EBOFeatures[] },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./components/places/states/state-grid/state-grid.component').then((m) => m.StateGridComponent),
            pathMatch: 'full',
            data: { title: 'States: [View]', flags: ['core'] as EBOFeatures[] }
          },
          {
            path: ':id',
            loadComponent: () =>
              import('./components/places/states/state-edit/states-edit.component').then((m) => m.StateEditComponent),
            canDeactivate: [CanDeactivateGuard],
            data: { title: 'States: [Entry]', flags: ['core'] as EBOFeatures[] }
          }
        ]
      },
      {
        path: 'sync',
        canActivate: [LoginGuard, FlagGuard],
        data: { flags: ['core'] as EBOFeatures[] },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./modules/sync/sync-snapshot-grid.component').then((m) => m.SyncSnapshotGridComponent),
            canDeactivate: [CanDeactivateGuard],
            data: { title: 'Sync Snapshot: [View]', flags: ['core'] as EBOFeatures[] }
          },
          {
            path: 'settings',
            //component: SyncSettingsComponent,
            redirectTo: '/dashboard',
            canDeactivate: [CanDeactivateGuard],
            data: { title: 'Sync : [View]', flags: ['core'] as EBOFeatures[] }
          }
        ]
      },
      {
        path: 'tenants',
        canActivate: [LoginGuard, FlagGuard],
        data: { flags: ['core'] as EBOFeatures[] },
        children: [
          {
            path: 'invoices/:id',
            loadComponent: () =>
              import('./modules/tenants/components/tenant-invoice-view/tenant-invoice-view.component').then(
                (m) => m.TenantInvoiceViewComponent
              ),
            canDeactivate: [CanDeactivateGuard],
            data: { title: 'Invoice: [Entry]', flags: ['core'] as EBOFeatures[] }
          },
          {
            path: 'account/billing',
            loadComponent: () =>
              import('./modules/tenants/components/tenant-invoice-page/tenant-invoice-page.component').then(
                (m) => m.TenantInvoicePageComponent
              ),
            data: { title: 'Account & Billing: [View]', flags: ['core'] as EBOFeatures[] }
          },
          {
            path: 'invoices',
            loadComponent: () =>
              import('./modules/tenants/components/tenant-invoice-grid/tenant-invoice-grid.component').then(
                (m) => m.TenantInvoiceGridComponent
              ),
            data: { title: 'Invoices: [View]', flags: ['core'] as EBOFeatures[] }
          },
          {
            path: '',
            loadComponent: () =>
              import('./modules/tenants/components/tenant-grid/tenant-grid.component').then(
                (m) => m.TenantGridComponent
              ),
            pathMatch: 'full',
            data: { title: 'Tenants: [View]', flags: ['core'] as EBOFeatures[] }
          },
          {
            path: ':id',
            loadComponent: () =>
              import('./modules/tenants/components/tenant-edit/tenant-edit.component').then(
                (m) => m.TenantEditComponent
              ),
            canDeactivate: [CanDeactivateGuard],
            data: { title: 'Tenant: [Entry]', flags: ['core'] as EBOFeatures[] }
          }
        ]
      },
      {
        path: 'hoslogs',
        canActivate: [LoginGuard, FlagGuard],
        data: { flags: ['core'] as EBOFeatures[] },
        children: [
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () =>
              import('./modules/time-log/components/time-log-grid/time-log-grid.component').then(
                (m) => m.TimeLogGridComponent
              )
          },
          {
            path: ':id',
            loadComponent: () =>
              import('./modules/time-log/components/time-log-edit/time-log-edit.component').then(
                (m) => m.TimeLogEditComponent
              ),
            data: { flags: ['core'] as EBOFeatures[] }
          }
        ]
      },
      //listing twice to allow for different routes
      {
        path: 'asks',
        canActivate: [LoginGuard, FlagGuard],
        canDeactivate: [CanDeactivateGuard],
        data: { flags: ['driverLogs'] as EBOFeatures[] },
        children: [
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () =>
              import(`./modules/digital-logs/components/asks/ask-grid/ask-grid.component`).then(
                (m) => m.AskGridComponent
              ),
            data: { preload: true, title: 'Logs: [View]', flags: ['driverLogs'] as EBOFeatures[] }
          },
          {
            path: 'bulk',
            pathMatch: 'full',
            loadComponent: () =>
              import(`./modules/digital-logs/components/asks/ask-bulk/ask-bulk.component`).then(
                (m) => m.AskBulkComponent
              ),
            data: { preload: true, title: 'Bulk', flags: ['driverLogs'] as EBOFeatures[] }
          },
          {
            path: 'split',
            pathMatch: 'full',
            redirectTo: '/dashboard'
          },
          {
            path: ':id',
            loadComponent: () =>
              import('./modules/digital-logs/components/asks/ask-edit/ask-edit.component').then(
                (c) => c.AskEditComponent
              ),
            canDeactivate: [CanDeactivateGuard],
            data: { title: 'Log: [Entry]', flags: ['driverLogs'] as EBOFeatures[], preload: true }
          }
        ]
      },
      //listing twice to allow for different routes
      {
        path: 'trips',
        canActivate: [LoginGuard, FlagGuard],
        canDeactivate: [CanDeactivateGuard],
        data: { flags: ['driverLogs'] as EBOFeatures[] },
        children: [
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () =>
              import(`./modules/digital-logs/components/asks/ask-grid/ask-grid.component`).then(
                (m) => m.AskGridComponent
              ),
            data: { preload: true, title: 'Logs: [View]', flags: ['driverLogs'] as EBOFeatures[] }
          },
          {
            path: 'bulk',
            pathMatch: 'full',
            loadComponent: () =>
              import(`./modules/digital-logs/components/asks/ask-bulk/ask-bulk.component`).then(
                (m) => m.AskBulkComponent
              ),
            data: { preload: true, title: 'Bulk', flags: ['driverLogs'] as EBOFeatures[] }
          },
          {
            path: 'split',
            pathMatch: 'full',
            redirectTo: '/dashboard'
          },
          {
            path: ':id',
            loadComponent: () =>
              import('./modules/digital-logs/components/asks/ask-edit/ask-edit.component').then(
                (c) => c.AskEditComponent
              ),
            canDeactivate: [CanDeactivateGuard],
            data: { title: 'Log: [Entry]', flags: ['driverLogs'] as EBOFeatures[], preload: true }
          }
        ]
      },
      {
        path: 'trucks',
        canActivate: [LoginGuard, FlagGuard],
        loadComponent: () =>
          import('./modules/trucks/components/truck-grid/truck-grid.component').then((m) => m.TruckGridComponent),
        pathMatch: 'full',
        data: { title: 'Trucks: [View]', flags: ['core'] as EBOFeatures[], preload: EboPermissions.PermissionsMechanic }
      },
      {
        path: 'trucks/expenses',
        loadComponent: () =>
          import('./modules/trucks/truck-expense-grid.component').then((m) => m.TruckExpenseGridComponent),
        canActivate: [FlagGuard],
        pathMatch: 'full',
        data: { title: 'Truck Expenses: [View]', flags: ['core'] as EBOFeatures[] }
      },
      {
        path: 'trucks/expenses/:id',
        loadComponent: () =>
          import('./modules/trucks/truck-expense-edit/truck-expense-edit.component').then(
            (m) => m.TruckExpenseEditComponent
          ),
        canDeactivate: [CanDeactivateGuard],
        data: { title: 'Truck Expense: [Entry]', flags: ['core'] as EBOFeatures[] }
      },
      {
        path: 'trucks/:id',
        loadComponent: () =>
          import('./modules/trucks/truck-edit/truck-edit.component').then((m) => m.TruckEditComponent),
        canDeactivate: [CanDeactivateGuard],
        data: { title: 'Truck: [Entry]', flags: ['core'] as EBOFeatures[], preload: EboPermissions.PermissionsMechanic }
      },

      {
        path: 'vacationaccruals',
        canActivate: [LoginGuard, FlagGuard],
        data: { preload: true, flags: ['payroll'] as EBOFeatures[] },
        children: [
          {
            path: '',
            loadComponent: () =>
              import(`./modules/payroll/components/vacation-accrual-grid/vacation-accrual-grid.component`).then(
                (m) => m.VacationAccrualGridComponent
              ),
            pathMatch: 'full',
            data: { title: 'Vacation Accrual: [View]', flags: ['payroll'] as EBOFeatures[] }
          },
          {
            path: ':id',
            loadComponent: () =>
              import(`./modules/payroll/components/vacation-accrual-edit/vacation-accrual-edit.component`).then(
                (m) => m.VacationAccrualEditComponent
              ),
            canDeactivate: [CanDeactivateGuard],
            data: { title: 'Vacation Accrual: [Entry]', flags: ['payroll'] as EBOFeatures[] }
          }
        ]
      },
      {
        path: 'vacations',
        canActivate: [LoginGuard, FlagGuard],
        data: { preload: true, flags: ['payroll'] as EBOFeatures[] },
        children: [
          {
            path: '',
            loadComponent: () =>
              import(`./modules/payroll/components/vacations-grid/vacation-grid.component`).then(
                (m) => m.VacationGridComponent
              ),
            pathMatch: 'full',
            data: {
              title: 'Vacations: [View]',
              flags: ['payroll'] as EBOFeatures[],
              preload: EboPermissions.PermissionsPayroll
            }
          },
          {
            path: ':id',
            loadComponent: () =>
              import(`./modules/payroll/components/vacations-edit/vacations-edit.component`).then(
                (m) => m.VacationsEditComponent
              ),
            canDeactivate: [CanDeactivateGuard],
            data: {
              title: 'Vacations: [Entry]',
              flags: ['payroll'] as EBOFeatures[],
              preload: EboPermissions.PermissionsPayroll
            }
          }
        ]
      },
      {
        path: 'error',
        children: [
          {
            path: '400',
            loadComponent: () => import(`@summit/ui`).then((m) => m.Error400Component),
            pathMatch: 'full',
            data: { title: '400: Not Found', preload: true }
          },
          {
            path: '',
            loadComponent: () => import(`@summit/ui`).then((m) => m.Error404Component),
            pathMatch: 'full',
            data: { title: '404: Not Found', preload: true }
          }
        ],
        data: { preload: true }
      },
      {
        path: '**',
        loadComponent: () => import(`@summit/ui`).then((m) => m.Error404Component),
        data: { preload: true }
      }
    ]
  }
];
